import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Loader2, Calendar as CalendarIcon, MapPin, Plus, X, Sparkles, AlertCircle } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const MIN_DAYS = 14;

function minDateLocal() {
  const d = new Date();
  d.setDate(d.getDate() + MIN_DAYS);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function EventCreator({ open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [dates, setDates] = useState(["", "", ""]);
  const [submitting, setSubmitting] = useState(false);

  const min = useMemo(() => minDateLocal(), []);

  const reset = () => {
    setTitle(""); setDescription(""); setLocation(""); setDates(["", "", ""]);
  };

  const handleClose = () => { if (!submitting) { reset(); onClose(); } };

  const updateDate = (i, v) => {
    const next = [...dates];
    next[i] = v;
    setDates(next);
  };

  const addDate = () => {
    if (dates.length < 4) setDates([...dates, ""]);
  };

  const removeDate = (i) => {
    if (dates.length > 2) setDates(dates.filter((_, idx) => idx !== i));
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Inserisci il titolo del Ritrovo");
    const filled = dates.filter((d) => d.trim()).map((d) => new Date(d));
    if (filled.length < 2) return toast.error("Servono almeno 2 date alternative");

    const minDateMs = Date.now() + MIN_DAYS * 24 * 3600 * 1000;
    for (const d of filled) {
      if (d.getTime() < minDateMs) {
        return toast.error(`Ogni data deve essere almeno tra ${MIN_DAYS} giorni`);
      }
    }

    setSubmitting(true);
    try {
      const res = await api.post("/events", {
        title: title.trim(),
        description: description.trim(),
        location: location.trim(),
        date_options: filled.map((d) => d.toISOString()),
      });
      toast.success("Ritrovo creato!");
      reset();
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore creazione evento");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        data-testid="event-creator-dialog"
        className="max-w-lg rounded-3xl border-[#E8E1D9] bg-white max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="mb-2 inline-flex w-fit items-center gap-2 rounded-full bg-red-50 px-3 py-1">
            <Sparkles className="h-3 w-3 text-red-600" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-red-700">
              Nuovo Ritrovo
            </span>
          </div>
          <DialogTitle className="font-display text-2xl font-bold text-[#2D2926]">
            Crea uno ZAP
          </DialogTitle>
          <DialogDescription className="text-[#5C5855]">
            Proponi 2–4 date alternative. Gli invitati voteranno la preferita. Minimo {MIN_DAYS} giorni di anticipo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
              Titolo *
            </label>
            <Input
              data-testid="event-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Es. Ritrovo di primavera"
              className="rounded-xl border-[#E8E1D9] bg-white"
            />
          </div>

          <div>
            <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
              Dettagli
            </label>
            <Textarea
              data-testid="event-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Cosa portiamo? Orario? Note..."
              rows={2}
              className="rounded-xl border-[#E8E1D9] bg-white resize-none"
            />
          </div>

          <div>
            <label className="mb-2 flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
              <MapPin className="h-3 w-3" /> Luogo
            </label>
            <Input
              data-testid="event-location-input"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Es. Casa nonni, Trattoria Da Mario..."
              className="rounded-xl border-[#E8E1D9] bg-white"
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
                <CalendarIcon className="h-3 w-3" /> Date alternative ({dates.length}/4)
              </label>
              {dates.length < 4 && (
                <button
                  data-testid="event-add-date-button"
                  type="button"
                  onClick={addDate}
                  className="flex items-center gap-1 rounded-full bg-[#FF7C43]/10 px-3 py-1 text-xs font-semibold text-[#FF7C43] hover:bg-[#FF7C43]/20"
                >
                  <Plus className="h-3 w-3" /> Aggiungi
                </button>
              )}
            </div>
            <div className="space-y-2">
              {dates.map((d, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FF7C43] text-xs font-bold text-white">
                    {i + 1}
                  </div>
                  <input
                    data-testid={`event-date-${i}`}
                    type="datetime-local"
                    value={d}
                    min={min}
                    onChange={(e) => updateDate(i, e.target.value)}
                    className="flex-1 rounded-xl border border-[#E8E1D9] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF7C43]/30"
                  />
                  {dates.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeDate(i)}
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E8E1D9] text-[#C87941] hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
              <p className="text-xs text-amber-800">
                <strong>Regola degli ZAP:</strong> ogni data deve essere proposta con almeno {MIN_DAYS} giorni di anticipo.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-row justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={submitting}
            className="rounded-full border-[#E8E1D9]"
          >
            Annulla
          </Button>
          <Button
            data-testid="event-create-submit"
            type="button"
            onClick={submit}
            disabled={submitting}
            className="rounded-full bg-red-600 text-white hover:bg-red-700"
          >
            {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Lancia lo ZAP
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
